import breadcrumbs from 'assets/img/breadcrumbs.svg';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import { TMediaFileType } from 'types';
import {
	StyleSettingsCarouselUploaderContext,
	IStyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import ModalLayout from 'components/ModalLayout';
import React, { useContext, useRef, useState, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import style from './CropperModal.module.scss';

interface ICropperModal {
	imageOrientation?: 'horizontal' | 'vertical';
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxFileSizeMB?: number;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	onUploadItem: (mediaFileType: TMediaFileType, sourceUrl: string, fileId: string) => void;
}

const CropperModal: React.FC<ICropperModal> = ({
	imageOrientation,
	horizontalAspectRatio,
	verticalAspectRatio,
	maxFileSizeMB,
	showGroupLoader,
	hideGroupLoader,
	onUploadItem,
}) => {
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setCropperSrc,
		uploadImage,
		cropperSrc,
		typeOfModalWindow,
	} = useContext<IStyleSettingsCarouselUploaderContext>(StyleSettingsCarouselUploaderContext);
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const [isCropAreaChanged, setCropAreaChanged] = useState<boolean>(false);
	const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [allowFileUpload, setAllowFileUpload] = useState<boolean>(true);
	const [imageFile, setImageFile] = useState<File | null>(null);

	// TODO think about it later.
	const [dataToUpload, setDataToUpload] = useState<{
		status: 'success' | 'error';
		sourceUrl?: string | null;
		fileId?: string | null;
	}>();

	useEffect(() => {
		if (dataToUpload?.status === 'success' && dataToUpload.sourceUrl && dataToUpload.fileId) {
			onUploadItem(1, dataToUpload.sourceUrl, dataToUpload.fileId);
			hideGroupLoader();
		} else if (dataToUpload?.status === 'error') {
			hideGroupLoader();
			changeTypeOfErrorModalWindow('fileUploadError');
		}
		setDataToUpload(undefined);
		setAllowFileUpload(true);
	}, [dataToUpload]);
	// TODO think about it later.

	const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = e.target.files ? e.target.files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB) {
			changeTypeOfErrorModalWindow('imageFileSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			const sourceUrl = URL.createObjectURL(imgFile);
			setCropperSrc(sourceUrl);
			setImageFile(imgFile);
		} else {
			changeTypeOfErrorModalWindow('invalidFileFormat');
		}
		e.target.value = '';
	};

	const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (canvasRef.current && imageRef.current) {
			canvasRef.current.width = imageRef.current.naturalWidth;
			canvasRef.current.height = imageRef.current.naturalHeight;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
		}
	};

	const handleCropAreaChange = (c: PixelCrop) => {
		setCrop(c);
		isSentOriginalImg && setSentOriginalImg(false);
		if (canvasRef.current && imageRef.current) {
			setCropAreaChanged(true);
			canvasRef.current.width = c.width;
			canvasRef.current.height = c.height;
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(
				imageRef.current,
				c.x * scaleX,
				c.y * scaleY,
				c.width * scaleX,
				c.height * scaleY,
				0,
				0,
				c.width,
				c.height,
			);
		}
	};

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setCrop(undefined);
		setCropAreaChanged(false);
		setCropperSrc(null);
		setImageFile(null);
	};

	// TODO think about it later.
	const handleApply = () => setAllowFileUpload(false);

	const handleApplyImageSize = () => {
		if (canvasRef.current?.width === 0 && canvasRef.current.height === 0 && imageFile) {
			(async () => {
				const sourceUrl = URL.createObjectURL(imageFile);
				const fileId = await uploadImage(imageFile);
				fileId
					? setDataToUpload({ status: 'success', sourceUrl, fileId })
					: setDataToUpload({ status: 'error' });
			})();
		} else if (isSentOriginalImg && imageFile) {
			(async () => {
				const sourceUrl = URL.createObjectURL(imageFile);
				const fileId = await uploadImage(imageFile);
				fileId
					? setDataToUpload({ status: 'success', sourceUrl, fileId })
					: setDataToUpload({ status: 'error' });
			})();
		} else {
			canvasRef.current?.toBlob(
				async (blob) => {
					if (blob) {
						const sourceUrl = URL.createObjectURL(blob);
						const fileId = await uploadImage(blob);
						fileId
							? setDataToUpload({ status: 'success', sourceUrl, fileId })
							: setDataToUpload({ status: 'error' });
					}
				},
				'image/jpeg',
				0.8,
			);
		}
	};

	useEffect(() => {
		if (!allowFileUpload) {
			closeModal();
			showGroupLoader();
			handleApplyImageSize();
		}
	}, [allowFileUpload]);
	// TODO think about it later.

	const renderImage = () => {
		if (!cropperSrc) {
			return (
				<div className={style.noImageContainer}>
					<svg className={style.noImageIcon}>
						<use xlinkHref={`${breadcrumbs}#twoHills`} />
					</svg>
				</div>
			);
		}

		return (
			<ReactCrop
				className={style.imageContainer}
				crop={crop}
				aspect={aspectRatio}
				onChange={handleCropAreaChange}>
				<img ref={imageRef} src={cropperSrc} onLoad={drawImageInCanvas} alt="Photo" />
			</ReactCrop>
		);
	};

	return (
		<ModalLayout width="60%" isVisible={typeOfModalWindow === 'cropper'} setIsShowModal={closeModal}>
			<div className={style.content}>
				{renderImage()}
				<input
					id="imageLoader"
					className={style.uploadInput}
					type="file"
					accept="image/jpeg,image/png"
					onChange={handleFileChange}
				/>
				<div className={style.buttons}>
					<label className={style.uploadLabel} htmlFor="imageLoader">
						<Button className={style.uploadButton} data-test="chooseImageButton">
							<Text translationNameSpace="form" translationKey="chooseImageButton" asFragment={true} />
						</Button>
					</label>
					<Button
						onClick={handleApply}
						disabled={!cropperSrc || !isCropAreaChanged || !allowFileUpload}
						data-test="applyButton">
						<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
					</Button>
				</div>
			</div>
			<canvas className={style.canvas} ref={canvasRef} />
		</ModalLayout>
	);
};

export default CropperModal;
